import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { libraryFinesApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { Fine, FineStatus } from '@/types/library';
import { formatDate, orDash } from '@/utils/format';
import { Badge, Field, FineStatusBadge, Money, humanise } from './shared';

const STATUSES: { value: FineStatus; label: string }[] = [
  { value: 'OUTSTANDING', label: 'Owed' },
  { value: 'PARTIALLY_SETTLED', label: 'Part settled' },
  { value: 'PAID', label: 'Paid' },
  { value: 'WAIVED', label: 'Waived' },
  { value: 'CANCELLED', label: 'Withdrawn' },
];

/**
 * Fines.
 *
 * <p>A student's fine is a charge on their fee account and is paid through the
 * fees screen — this page shows it but cannot take the money, because two places
 * accepting payment is two records that will eventually disagree about whether a
 * family has paid. A staff fine is settled at the desk, because the fee ledger
 * only knows about students.
 *
 * <p>Nothing here deletes a fine. Waiving records who forgave it and why, and
 * withdrawing is a different thing that says the debt never existed at all.
 * Waiving needs its own permission, which the librarian role does not hold.
 */
export function LibraryFinesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('chargedOn,desc');

  const manages = can(PERMISSIONS.LIBRARY_FINE_MANAGE);
  const waives = can(PERMISSIONS.LIBRARY_FINE_WAIVE);

  const [status, setStatus] = useState<FineStatus | ''>('');
  const [viewing, setViewing] = useState<Fine | null>(null);
  const [settling, setSettling] = useState<{ fine: Fine; mode: 'payment' | 'waiver' } | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');

  const params = { ...table.params, ...(status === '' ? {} : { status: [status] }) };

  const fines = useQuery({
    queryKey: queryKeys.libraryFines.list(params),
    queryFn: () => libraryFinesApi.list(params),
  });

  const detail = useQuery({
    queryKey: queryKeys.libraryFines.detail(viewing?.id ?? ''),
    queryFn: () => libraryFinesApi.get(viewing!.id),
    enabled: viewing !== null,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.libraryFines.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.libraryMembers.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.library.all });
  };

  const settle = useMutation({
    mutationFn: () => {
      const payload = {
        amount: amount.trim() === '' ? undefined : Number(amount),
        reason: reason.trim(),
        reference: reference.trim() === '' ? undefined : reference.trim(),
      };
      return settling!.mode === 'payment'
        ? libraryFinesApi.recordPayment(settling!.fine.id, payload)
        : libraryFinesApi.waive(settling!.fine.id, payload);
    },
    onSuccess: (fine) => {
      toast.success(
        `${fine.fineNumber} is now ${humanise(fine.status)}${
          fine.outstanding > 0 ? ` — ${fine.outstanding} still owed` : ''
        }`,
      );
      setSettling(null);
      setAmount('');
      setReason('');
      setReference('');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const cancel = useMutation({
    mutationFn: ({ id, why }: { id: string; why: string }) => libraryFinesApi.cancel(id, why),
    onSuccess: (fine) => {
      toast.success(`${fine.fineNumber} withdrawn`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<Fine>[] = [
    {
      key: 'fineNumber',
      header: 'Fine',
      render: (row) => (
        <span>
          <span className="block font-mono text-[12px] text-ink">{row.fineNumber}</span>
          <span className="block text-[11px] text-subtle">{formatDate(row.chargedOn)}</span>
        </span>
      ),
    },
    {
      key: 'member',
      header: 'Member',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.memberName}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.membershipNumber}</span>
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Why',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.reason}</span>
          <span className="block text-[11px] text-subtle">{humanise(row.fineType)}</span>
        </span>
      ),
    },
    {
      key: 'amount',
      header: 'Charged',
      sortKey: 'amount',
      className: 'text-right',
      render: (row) => <Money value={row.amount} />,
    },
    {
      key: 'outstanding',
      header: 'Still owed',
      className: 'text-right',
      render: (row) => (
        <span>
          <Money value={row.outstanding} bold />
          {row.settlementMode === 'FINANCE' && row.financeOutstanding !== undefined && (
            // Finance is authoritative for a student — they may have paid
            // through the fees screen since this fine was raised.
            <span className="block text-[11px] text-subtle">
              fees say {row.financeOutstanding}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'settlement',
      header: 'Settles',
      render: (row) => (
        <Badge tone={row.settlementMode === 'FINANCE' ? 'info' : 'neutral'}>
          {row.settlementMode === 'FINANCE' ? 'on fees' : 'at the desk'}
        </Badge>
      ),
    },
    { key: 'status', header: 'Status', render: (row) => <FineStatusBadge status={row.status} /> },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) => {
        const open = row.status === 'OUTSTANDING' || row.status === 'PARTIALLY_SETTLED';
        return (
          <span className="flex justify-end gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => setViewing(row)}>
              View
            </Button>
            {open && manages && row.settlementMode === 'DIRECT' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSettling({ fine: row, mode: 'payment' })}
              >
                Take payment
              </Button>
            )}
            {open && waives && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSettling({ fine: row, mode: 'waiver' })}
              >
                Waive
              </Button>
            )}
            {row.status === 'OUTSTANDING' && waives && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const why = window.prompt(
                    'Withdrawing says the fine should never have been raised. Why?',
                  );
                  if (why !== null && why.trim() !== '') {
                    cancel.mutate({ id: row.id, why: why.trim() });
                  }
                }}
              >
                Withdraw
              </Button>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <ListPage
        title="Fines"
        description="What is owed, what was collected, and what the school chose to forgive."
        icon={<Coins className="h-5 w-5" aria-hidden="true" />}
        filters={
          <FilterSelect
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              table.setPage(0);
            }}
            options={STATUSES}
          />
        }
        pagination={fines.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={fines.data?.data}
          rowKey={(row) => row.id}
          loading={fines.isPending}
          error={fines.error}
          onRetry={() => void fines.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No fines"
          emptyDescription="Nobody has been fined."
        />
      </ListPage>

      <Modal
        open={settling !== null}
        onClose={() => setSettling(null)}
        title={settling?.mode === 'payment' ? 'Record a payment' : 'Waive a fine'}
        description={
          settling?.mode === 'payment'
            ? 'Money taken at the desk.'
            : 'The fine stays on the record with your reason against it. Nothing is deleted.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setSettling(null)}>
              Cancel
            </Button>
            <Button onClick={() => settle.mutate()} disabled={reason.trim() === '' || settle.isPending}>
              {settling?.mode === 'payment' ? 'Record it' : 'Waive it'}
            </Button>
          </>
        }
      >
        {settling !== null && (
          <div className="space-y-3">
            <dl>
              <Field label="Fine">{settling.fine.fineNumber}</Field>
              <Field label="Member">{settling.fine.memberName}</Field>
              <Field label="Still owed">
                <Money value={settling.fine.outstanding} bold />
              </Field>
            </dl>
            <TextField
              label="Amount"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              hint="Leave it blank to settle the whole balance."
            />
            <TextField
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
              hint={
                settling.mode === 'payment'
                  ? 'What was taken, and how.'
                  : 'Required. A waiver nobody can explain is a waiver nobody can defend.'
              }
            />
            {settling.mode === 'payment' && (
              <TextField
                label="Receipt number"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title={viewing?.fineNumber ?? 'Fine'}
        description="Every change since it was raised."
        size="lg"
      >
        {detail.data !== undefined && (
          <div>
            <dl>
              <Field label="Member">{detail.data.memberName}</Field>
              <Field label="Why">{detail.data.reason}</Field>
              <Field label="Charged">{formatDate(detail.data.chargedOn)}</Field>
              <Field label="Amount">
                <Money value={detail.data.amount} />
              </Field>
              <Field label="Paid">
                <Money value={detail.data.paidAmount} />
              </Field>
              <Field label="Waived">
                <Money value={detail.data.waivedAmount} />
              </Field>
              <Field label="Still owed">
                <Money value={detail.data.outstanding} bold />
              </Field>
              <Field label="Settles">
                {detail.data.settlementMode === 'FINANCE'
                  ? 'As a charge on the fee account'
                  : 'At the library desk'}
              </Field>
              {detail.data.daysOverdue !== undefined && (
                <Field label="Days late">
                  {detail.data.daysOverdue} at {orDash(detail.data.ratePerDay)} a day
                </Field>
              )}
            </dl>

            <h3 className="mt-4 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
              History
            </h3>
            {detail.data.adjustments.length === 0 ? (
              <p className="py-3 text-sm text-muted">Nothing has been paid or waived.</p>
            ) : (
              <ul className="mt-1 divide-y divide-line">
                {detail.data.adjustments.map((adjustment) => (
                  <li key={adjustment.id} className="flex items-baseline justify-between gap-3 py-2">
                    <span className="min-w-0">
                      <span className="block text-sm text-ink">{humanise(adjustment.type)}</span>
                      <span className="block text-[11px] text-subtle">{adjustment.reason}</span>
                    </span>
                    <span className="shrink-0 text-right">
                      <Money value={adjustment.amount} />
                      <span className="block text-[11px] text-subtle">
                        {formatDate(adjustment.adjustedAt)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
